import { toPng } from 'html-to-image'
import { ColorModeEnum } from '@ztechapps/common.hook'
import { getWindow } from '@ztechapps/common.utils'

const EXPORT_PIXEL_RATIO = 2
const COLOR_MODE_ATTRIBUTE = 'data-bs-theme'
// px of empty margin the PNG keeps around the chart, so the top tick label does not touch the edge
const EXPORT_PADDING = 16
// pt of empty margin the PDF page keeps around the image
const PDF_MARGIN = 24
// the wrapper recharts draws the legend in, absolutely positioned and anchored at the chart's bottom edge
const LEGEND_SELECTOR = '.recharts-legend-wrapper'

/**
 * Lifts the on-screen height cap off the chart's legend for the capture, so every entry is in the image. The chart
 * reserved only the capped height for the legend, and the legend is anchored at the bottom edge, so lifting the cap
 * alone would grow it up over the plot. Moving its bottom edge down by the hidden height keeps its top where it was
 * and grows it below the chart box instead. `hiddenHeight` is how far the legend then reaches below the chart box
 * (0 with no legend, or one that fits its cap), and `restore` puts the cap back.
 */
export const uncapChartLegend = (element: HTMLElement): { hiddenHeight: number; restore: () => void } => {
  const legend = element.querySelector<HTMLElement>(LEGEND_SELECTOR)
  if (!legend) return { hiddenHeight: 0, restore: () => undefined }
  const hiddenHeight = Math.max(legend.scrollHeight - legend.clientHeight, 0)
  const { maxHeight, overflowY, bottom } = legend.style
  legend.style.maxHeight = 'none'
  legend.style.overflowY = 'visible'
  legend.style.bottom = `calc(${bottom || '0px'} - ${hiddenHeight}px)`
  return {
    hiddenHeight,
    restore: () => {
      legend.style.maxHeight = maxHeight
      legend.style.overflowY = overflowY
      legend.style.bottom = bottom
    },
  }
}

/**
 * Renders the chart element to a PNG data URL. The element is forced to the light colour scheme for the capture
 * and the image is backed by that scheme's body background, so the export is a light document in either colour
 * mode and dark-mode text stays legible, and its legend is uncapped. Pass the full-width chart element, never the
 * clipped scroll viewport around it. Returns the image with the size it was captured at.
 */
const renderChartPngDataUrl = async (
  element: HTMLElement
): Promise<{ dataUrl: string; width: number; height: number }> => {
  element.setAttribute(COLOR_MODE_ATTRIBUTE, ColorModeEnum.light)
  const { hiddenHeight, restore } = uncapChartLegend(element)
  // measured before the awaits — the element can leave the DOM while the image renders
  const width = element.offsetWidth + 2 * EXPORT_PADDING
  const height = element.offsetHeight + hiddenHeight + 2 * EXPORT_PADDING
  try {
    // read from the element, under the light override: Bootstrap re-declares --bs-body-bg per colour scheme, so the
    // document root would hand back the dark value in dark mode
    const backgroundColor = getWindow().getComputedStyle(element).getPropertyValue('--bs-body-bg').trim()
    // awaited so the attribute is still set while the library clones the node and reads its styles
    const dataUrl = await toPng(element, {
      backgroundColor,
      pixelRatio: EXPORT_PIXEL_RATIO,
      // the library sizes the image from the element, so the padding needs the box grown by its two sides, and the
      // height by the legend's hidden part
      width,
      height,
      style: { padding: `${EXPORT_PADDING}px` },
    })
    return { dataUrl, width, height }
  } finally {
    restore()
    element.removeAttribute(COLOR_MODE_ATTRIBUTE)
  }
}

/** Renders the chart element to a PNG Blob — the rendered data URL read back as bytes. */
export const exportChartPng = async (element: HTMLElement): Promise<Blob> => {
  const { dataUrl } = await renderChartPngDataUrl(element)
  // the platform fetch: the `fetch` exported by common.utils parses the body as JSON, so it has no `.blob()`
  const response = await getWindow().fetch(dataUrl)
  return response.blob()
}

/**
 * Renders the chart element to a one-page landscape A4 PDF Blob, the chart image scaled to fit inside the page margin
 * with its aspect ratio kept and centred in it. A fixed page keeps a wide chart under jsPDF's 14,400-unit page-size
 * limit.
 * `jspdf` loads here, on the first PDF export, never in the initial bundle.
 */
export const exportChartPdf = async (element: HTMLElement): Promise<Blob> => {
  const { dataUrl, width, height } = await renderChartPngDataUrl(element)
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const scale = Math.min((pageWidth - 2 * PDF_MARGIN) / width, (pageHeight - 2 * PDF_MARGIN) / height)
  // 'FAST' compresses the image; without it jsPDF embeds the raw pixels, megabytes for one chart
  doc.addImage({
    imageData: dataUrl,
    format: 'PNG',
    x: (pageWidth - width * scale) / 2,
    y: (pageHeight - height * scale) / 2,
    width: width * scale,
    height: height * scale,
    compression: 'FAST',
  })
  return doc.output('blob')
}
